import { useState, useEffect } from 'react';
import { axios } from '../App';
import { toast } from 'sonner';
import { Send, MessageSquare } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

export default function Communications() {
  const [messages, setMessages] = useState([]);
  const [content, setContent] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadMessages();
    const interval = setInterval(loadMessages, 10000);
    return () => clearInterval(interval);
  }, []);

  const loadMessages = async () => {
    try {
      const { data } = await axios.get('/messages');
      setMessages(data.reverse());
    } catch (error) {
      toast.error('Failed to load messages');
    } finally {
      setLoading(false);
    }
  };

  const handleSend = async (e) => {
    e.preventDefault();
    if (!content.trim()) return;

    try {
      await axios.post('/messages', { content, type: 'message' });
      setContent('');
      loadMessages();
    } catch (error) {
      toast.error('Failed to send message');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-sky-500 font-mono text-sm">CONNECTING TO COMMS...</div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col" data-testid="communications-page">
      <div className="bg-zinc-900/50 border-b border-zinc-800 p-6">
        <h1 className="text-2xl font-bold tracking-tight uppercase">Communications Hub</h1>
        <p className="text-xs text-zinc-500 uppercase tracking-wider mt-1 font-mono">
          Team coordination & broadcasts
        </p>
      </div>

      <div className="flex-1 overflow-y-auto p-6 space-y-4" data-testid="messages-container">
        {messages.length === 0 ? (
          <div className="text-center py-8 text-zinc-600 font-mono text-sm">
            No messages yet
          </div>
        ) : (
          messages.map((message) => (
            <div
              key={message.id}
              className="bg-zinc-950/80 border border-zinc-800 rounded-sm p-4 hover:border-zinc-700 transition-colors"
              data-testid={`message-${message.id}`}
            >
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-sky-500/10 border border-sky-500/30 rounded-sm flex items-center justify-center flex-shrink-0">
                  <MessageSquare size={18} className="text-sky-500" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-bold uppercase tracking-tight text-sky-500">
                      {message.sender}
                    </span>
                    <span className="text-xs font-mono text-zinc-600">
                      {new Date(message.created_at).toLocaleString()}
                    </span>
                  </div>
                  <p className="text-sm text-zinc-300">{message.content}</p>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      <div className="border-t border-zinc-800 p-6 bg-zinc-900/50">
        <form onSubmit={handleSend} className="flex gap-3" data-testid="message-form">
          <Input
            value={content}
            onChange={(e) => setContent(e.target.value)}
            placeholder="Type message..."
            className="flex-1 bg-zinc-900/50 border-zinc-800 rounded-none font-mono text-sm"
            data-testid="message-input"
          />
          <Button
            type="submit"
            disabled={!content.trim()}
            className="bg-sky-600 hover:bg-sky-500 text-white rounded-none uppercase tracking-wider font-bold text-xs border border-sky-400/20 hover:border-sky-400 px-6"
            data-testid="send-message-button"
          >
            <Send size={16} className="mr-2" />
            Send
          </Button>
        </form>
      </div>
    </div>
  );
}